import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/utils/ui";

export type TranscriptEntryData = {
  timestamp: string;
  speaker: string;
  speakerType: "doctor" | "patient";
  content: string;
  pauseDuration?: number;
};

type TranscriptEntryProps = {
  entry: TranscriptEntryData;
  isSearchResult?: boolean;
  isHighlighted?: boolean;
  onClick?: () => void;
  entryId?: string;
};

export const TranscriptEntry = ({
  entry,
  isSearchResult = false,
  isHighlighted = false,
  onClick,
  entryId,
}: TranscriptEntryProps) => {
  const { timestamp, speaker, content, pauseDuration } = entry;

  // Format content with bold tags for search results
  const formatContent = (text: string) => {
    if (!isSearchResult) return text;
    // Replace **text** with <strong>text</strong> for highlighting
    return text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  };

  const contentElement = (
    <div className="flex gap-4">
      <div className="flex-shrink-0">
        <Badge variant="primary">{timestamp}</Badge>
      </div>

      <div className="flex-1 space-y-1">
        <p className="text-xs font-semibold text-foreground">{speaker}:</p>
        <p
          className="text-xs leading-[18px] text-foreground"
          dangerouslySetInnerHTML={{ __html: formatContent(content) }}
        />
        {pauseDuration !== undefined && pauseDuration > 0 && (
          <p className="text-xs italic text-foreground-muted">
            (Pause: {pauseDuration.toFixed(1)}s)
          </p>
        )}
      </div>
    </div>
  );

  if (isSearchResult) {
    return (
      <Card
        className={cn(
          "p-4",
          onClick && "cursor-pointer transition-colors hover:bg-primary/5"
        )}
        onClick={onClick}
      >
        {contentElement}
      </Card>
    );
  }

  return (
    <div
      id={entryId}
      className={cn(
        "-mx-2 rounded-lg p-2 transition-colors",
        isHighlighted && "bg-primary/15 ring-1 ring-primary/30"
      )}
    >
      {contentElement}
    </div>
  );
};

type TranscriptEntryListProps = {
  entries: TranscriptEntryData[];
  filter: string;
  isSearchResults?: boolean;
  onEntryClick?: (entry: TranscriptEntryData) => void;
  highlightedEntryId?: string | null;
};

export const TranscriptEntryList = ({
  entries,
  filter,
  isSearchResults = false,
  onEntryClick,
  highlightedEntryId,
}: TranscriptEntryListProps) => {
  const filteredEntries = useMemo(() => {
    if (filter === "all") return entries;
    return entries.filter((entry) => entry.speaker === filter);
  }, [entries, filter]);

  if (filteredEntries.length === 0) {
    return (
      <p className="italic text-foreground-muted">
        No transcript entries available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {entries.map((entry, originalIndex) => {
        if (filter !== "all" && entry.speaker !== filter) return null;
        const entryId = isSearchResults
          ? undefined
          : `transcript-entry-${originalIndex}`;
        return (
          <TranscriptEntry
            key={`${entry.timestamp}-${originalIndex}`}
            entry={entry}
            isSearchResult={isSearchResults}
            isHighlighted={!!entryId && entryId === highlightedEntryId}
            onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
            entryId={entryId}
          />
        );
      })}
    </div>
  );
};
